
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Comprehensive reference for integrating with Plaid API endpoints'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='API Reference' subtitle={metadata.description} mdxType="Header" />
    <h4 {...{
      "id": "api-endpoints-and-webhooks"
    }}>{`API endpoints and webhooks`}</h4>
    <p>{`For documentation on specific API endpoints and webhooks, use the navigation menu or search.`}</p>
    <h4 {...{
      "id": "api-access"
    }}>{`API access`}</h4>
    <p>{`To gain access to the Plaid API, create an account on the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com"
      }}>{`Plaid Dashboard`}</a>{`. Once you've completed the signup process and acknowledged our terms, we'll provide a live `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`secret`}</inlineCode>{` via the Dashboard.`}</p>
    <h4 {...{
      "id": "api-protocols-and-headers"
    }}>{`API protocols and headers`}</h4>
    <p>{`The Plaid API is JSON over HTTP. Requests are POST requests, and responses are JSON, with errors indicated in response bodies as `}<inlineCode parentName="p">{`error_code`}</inlineCode>{` and `}<inlineCode parentName="p">{`error_type`}</inlineCode>{` (use these in preference to HTTP status codes for identifying application-level errors). All responses come as standard JSON, with a small subset returning binary data where appropriate. The Plaid API is served over HTTPS TLS v1.2 to ensure data privacy; HTTP and HTTPS with TLS versions other than 1.2 are not supported. Clients must use an up to date root certificate bundle as the only TLS verification path; certificate pinning should never be used. All requests must include a `}<inlineCode parentName="p">{`Content-Type`}</inlineCode>{` of `}<inlineCode parentName="p">{`application/json`}</inlineCode>{` and the body must be valid JSON. `}</p>
    <p>{`Almost all Plaid API endpoints require a `}<inlineCode parentName="p">{`client_id`}</inlineCode>{` and `}<inlineCode parentName="p">{`secret`}</inlineCode>{`. These may be sent either in the request body or in the headers `}<inlineCode parentName="p">{`PLAID-CLIENT-ID`}</inlineCode>{` and `}<inlineCode parentName="p">{`PLAID-SECRET`}</inlineCode>{`.`}</p>
    <p>{`Every Plaid API response includes a `}<inlineCode parentName="p">{`request_id`}</inlineCode>{`, either in the body or (in the case of endpoints that return binary data, such as `}<inlineCode parentName="p">{`/asset_report/pdf/get`}</inlineCode>{`) in the response headers. For faster support, include the `}<inlineCode parentName="p">{`request_id`}</inlineCode>{` when contacting support regarding a specific API call.`}</p>
    <h4 {...{
      "id": "api-host"
    }}>{`API host`}</h4>
    <pre><code parentName="pre" {...{
        "className": "language-text",
        "metastring": "switcher=false",
        "switcher": "false"
      }}>{`https://sandbox.plaid.com (Sandbox)
https://production.plaid.com (Production)
`}</code></pre>
    <p>{`Plaid has two environments: Sandbox and Production. Items cannot be moved between environments. The Sandbox environment supports only test Items. You can `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/overview/production"
      }}>{`request Production API access`}</a>{` via the Dashboard.`}</p>
    <h4 {...{
      "id": "api-status-and-incidents"
    }}>{`API status and incidents`}</h4>
    <p>{`API status is available at `}<a parentName="p" {...{
        "href": "https://status.plaid.com"
      }}>{`status.plaid.com`}</a>{`.`}</p>
    <p>{`API status and incidents are also available programmatically via the following endpoints:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://status.plaid.com/api/v2/status.json"
        }}>{`https://status.plaid.com/api/v2/status.json`}</a>{` for current status`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "https://status.plaid.com/api/v2/incidents.json"
        }}>{`https://status.plaid.com/api/v2/incidents.json`}</a>{` for current and historical incidents`}</li>
    </ul>
    <p>{`For a complete list of all API status information available programmatically, as well as more information on using these endpoints, see the `}<a parentName="p" {...{
        "href": "https://docs.incident.io/status-pages/api"
      }}>{`Incident.io Status API documentation`}</a>{`.`}</p>
    <p>{`For information on institution-specific insights, see `}<a parentName="p" {...{
        "href": "/docs/account/activity/#troubleshooting-institution-insights"
      }}>{`Troubleshooting institution insights`}</a>{`.`}</p>
    <h4 {...{
      "id": "storing-api-data"
    }}>{`Storing API data`}</h4>
    <p>{`Any token returned by the API is sensitive and should be stored securely. Except for the `}<inlineCode parentName="p">{`public_token`}</inlineCode>{` and `}<inlineCode parentName="p">{`link_token`}</inlineCode>{`, all Plaid tokens are long-lasting and should never be exposed on the client side. Consumer data obtained from the Plaid API is sensitive information and should be managed accordingly. For guidance and best practices on how to store and handle sensitive data, see the `}<a parentName="p" {...{
        "href": "https://security.plaid.com/documents?id=8967b9c3-1e20-407d-8549-f5e0b29786e7&search=ofdss"
      }}>{`Open Finance Data Security Standard`}</a>{`.`}</p>
    <p>{`Identifiers used by the Plaid API that do not contain consumer data and are not keys or tokens are designed for usage in less sensitive contexts. The most common of these identifiers are the `}<inlineCode parentName="p">{`account_id`}</inlineCode>{`, `}<inlineCode parentName="p">{`item_id`}</inlineCode>{`, `}<inlineCode parentName="p">{`link_session_id`}</inlineCode>{`, and `}<inlineCode parentName="p">{`request_id`}</inlineCode>{`. These identifiers are commonly used for logging and debugging purposes.`}</p>
    <h4 {...{
      "id": "api-field-formats"
    }}>{`API field formats`}</h4>
    <h5 {...{
      "id": "strings"
    }}>{`Strings`}</h5>
    <p>{`Many string fields returned by Plaid APIs are reported exactly as returned by the financial institution. For this reason, Plaid does not have maximum length limits or standardized formats for strings returned by the API. In practice, field lengths of 280 characters will generally be adequate for storing returned strings, although Plaid does not guarantee this as a maximum string length.`}</p>
    <h5 {...{
      "id": "numbers-and-money"
    }}>{`Numbers and money`}</h5>
    <p>{`Plaid returns all currency values as decimal values in dollars (or the equivalent for the currency being used), rather than as integers. In some cases, it may be possible for a money value returned by the Plaid API to have more than two digits of precision -- this is common, for example, when reporting crypto balances.`}</p>
    <h4 {...{
      "id": "openapi-definition-file"
    }}>{`OpenAPI definition file`}</h4>
    <p>{`OpenAPI is a standard format for describing RESTful APIs that allows those APIs to be integrated with tools for a wide variety of applications, including testing, client library generation, IDE integration, and more. The Plaid API is specified in our `}<a parentName="p" {...{
        "href": "https://github.com/plaid/plaid-openapi"
      }}>{`Plaid OpenAPI GitHub repo`}</a>{`.`}</p>
    <h4 {...{
      "id": "postman-collection"
    }}>{`Postman collection`}</h4>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://github.com/plaid/plaid-postman"
      }}>{`Postman collection`}</a>{` is a convenient tool for exploring Plaid API endpoints without writing code. The Postman collection provides pre-formatted requests for almost all of Plaid's API endpoints. All you have to do is fill in your API keys and any arguments. To get started, check out the `}<a parentName="p" {...{
        "href": "https://github.com/plaid/plaid-postman"
      }}>{`Plaid Postman Collection Quickstart`}</a>{` on GitHub.`}</p>
    <h4 {...{
      "id": "client-libraries"
    }}>{`Client libraries`}</h4>
    <p>{`See the `}<a parentName="p" {...{
        "href": "/docs/api/libraries"
      }}>{`client libraries`}</a>{` page for more information on Plaid's client libraries.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "api-endpoints-and-webhooks",
        level: 4,
        title: "API endpoints and webhooks",
      },
{
        id: "api-access",
        level: 4,
        title: "API access",
      },
{
        id: "api-protocols-and-headers",
        level: 4,
        title: "API protocols and headers",
      },
{
        id: "api-host",
        level: 4,
        title: "API host",
      },
{
        id: "api-status-and-incidents",
        level: 4,
        title: "API status and incidents",
      },
{
        id: "storing-api-data",
        level: 4,
        title: "Storing API data",
      },
{
        id: "api-field-formats",
        level: 4,
        title: "API field formats",
      },
{
        id: "openapi-definition-file",
        level: 4,
        title: "OpenAPI definition file",
      },
{
        id: "postman-collection",
        level: 4,
        title: "Postman collection",
      },
{
        id: "client-libraries",
        level: 4,
        title: "Client libraries",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
